import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Loader2, Brain, Book, Target, Clock } from 'lucide-react';
import styles from './LandingPage.module.css';

const LandingPage = ({ onGetStarted }) => {
  const navigate = useNavigate();
  const [topic, setTopic] = useState('');
  const [animate, setAnimate] = useState(false);
  const [visibleFeatures, setVisibleFeatures] = useState([]);
  const [currentPlaceholder, setCurrentPlaceholder] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const features = [
    {
      icon: <Brain size={24} />,
      title: 'AI-Powered Learning',
      description: 'Smart algorithms that help you learn faster'
    },
    {
      icon: <Book size={24} />,
      title: 'Comprehensive Notes',
      description: 'Generate detailed study materials instantly'
    },
    {
      icon: <Target size={24} />,
      title: 'Interactive Quizzes',
      description: 'Test your knowledge with dynamic assessments'
    },
    {
      icon: <Clock size={24} />,
      title: 'Time-Efficient',
      description: 'Learn faster with organized content'
    }
  ];

  const placeholders = [
    'Enter a topic like "Quantum Physics"',
    'Try "Machine Learning Basics"',
    'Maybe "World War II"',
    'Or "Chemical Bonds"',
    'How about "Ancient Civilizations"',
    'Perhaps "Human Biology"',
    'Try "Music Theory"',
    'Or "Space Exploration"'
  ];

  useEffect(() => {
    setAnimate(true);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.getAttribute('data-index'));
            setVisibleFeatures(prev => [...prev, index]);
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll(`.${styles.featureCard}`).forEach(card => {
      observer.observe(card);
    });

    const interval = setInterval(() => {
      setCurrentPlaceholder(current => 
        current === placeholders.length - 1 ? 0 : current + 1
      );
    }, 3000);

    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!topic.trim()) return;
    
    setIsLoading(true);
    try {
      await onGetStarted(topic);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <nav className={styles.nav}>
        <div className={styles.logo}>StudyAI</div>
        <button 
          onClick={() => navigate('/about')} 
          className={styles.button}
        >
          About Us
        </button>
      </nav>

      <main className={styles.main}>
        <h1 className={`${styles.title} ${animate ? styles.slideUpFade : ''}`}>
          Transform Your Learning with{' '}
          <span className={styles.highlight}>AI-Powered</span> Study Notes
        </h1>

        <p className={`${styles.subtitle} ${animate ? styles.fadeInDelayed : ''}`}>
          Generate comprehensive study materials and interactive quizzes instantly
        </p>

        <form 
          onSubmit={handleSubmit}
          className={`${styles.searchContainer} ${animate ? styles.scaleIn : ''} ${isLoading ? styles.loading : ''}`}
        >
          <input
            type="text"
            className={styles.input}
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder={placeholders[currentPlaceholder]}
            disabled={isLoading}
          />
          <button 
            type="submit" 
            className={`${styles.searchButton} ${topic.trim() ? styles.active : ''}`}
            disabled={isLoading || !topic.trim()}
          >
            {isLoading ? (
              <Loader2 size={20} className={styles.loadingIcon} />
            ) : (
              <ArrowRight size={20} />
            )}
          </button>
        </form>

        <div className={styles.featuresGrid}>
          {features.map((feature, index) => (
            <div 
              key={feature.title}
              className={`${styles.featureCard} ${
                visibleFeatures.includes(index) ? styles.fadeInUp : ''
              }`}
              data-index={index}
            >
              <div className={styles.featureIcon}>{feature.icon}</div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default LandingPage;